import { Map } from 'immutable';
import { combineActions, handleActions } from 'redux-actions';

import { resourceUpserters } from '../core';
import type { QcsSyncResourceStoreProps } from '../core/resourceStoreTypes';
import { deleteCreditProfileSuccess } from '../credit-profile/actions';
import * as actions from './actions';
import { type CreditReportStore, type CreditReportType, mkCreditReportStore } from './types';

const creditReportReducer = handleActions<
  CreditReportStore,
  | ReturnType<typeof deleteCreditProfileSuccess>
  | ReturnType<typeof actions.getCreditReportSuccess>
  | ReturnType<typeof actions.refreshCreditReportSuccess>
  | ReturnType<typeof actions.getRefreshedCreditReportSuccess>
  | ReturnType<typeof actions.clearOrphanedCreditReport>
>(
  {
    [combineActions(actions.getCreditReport, actions.refreshCreditReport).toString()]: (state: any) =>
      state.merge({ isLoading: true }),

    // QCS gives a refreshed report a new ID and the selector reads the first entry, so a fetched report replaces the
    // stored one. A response with no ID names no report, so the stored one stays.
    [actions.getCreditReportSuccess.type]: (state, { payload }) =>
      resourceUpserters.upsertResources<CreditReportType, QcsSyncResourceStoreProps<CreditReportType>>(state, {
        resources: payload.resources,
        replaceCollection: Boolean(payload.resources[0]?.id),
      }),

    [actions.refreshCreditReportSuccess.type]: (state, { payload }) =>
      resourceUpserters.upsertResource<CreditReportType, QcsSyncResourceStoreProps<CreditReportType>>(
        state.merge({ loadPending: false }),
        payload,
      ),

    // QCS deletes the old credit report and sends a new credit report with a new ID.
    // resetting the resourcesById before merging the new credit report.
    [actions.getRefreshedCreditReportSuccess.type]: (state, { payload }) =>
      resourceUpserters.upsertResource<CreditReportType, QcsSyncResourceStoreProps<CreditReportType>>(
        state.merge({ loadPending: false, resourcesById: Map() }),
        payload,
      ),

    [deleteCreditProfileSuccess.type]: () => mkCreditReportStore(),

    // Empty rather than reset: nothing will fetch a report without a profile, so loadPending must
    // stay false or the consumers sit on a skeleton forever.
    [actions.clearOrphanedCreditReport.type]: (state: any) =>
      state.merge({ resourcesById: Map(), lastSyncDate: null, loadPending: false, isLoading: false, error: null }),

    [combineActions(
      actions.getCreditReportFailure,
      actions.refreshCreditReportFailure,
      actions.getRefreshedCreditReportFailure,
    ).toString()]: (state: any, { payload }: any) => resourceUpserters.completeWithError(state, payload),
  },
  mkCreditReportStore(),
);

export default creditReportReducer;
